import BaseModal from 'components/Modals/BaseModal';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import RoadDefinerMap from 'features/Integrations/components/RoadDefinerMap.jsx';
import { tryParseString } from 'features/Integrations/utils/parser.utils';
import { useMemo, useState } from 'react';
import { Button, Form, OverlayTrigger } from 'react-bootstrap';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetLicenseInfoQuery } from 'services/integrations/integrations.api';
import DevicePairing, { CAMERA_TYPE, SIREN_TYPE } from './components/DevicePairing';
import ModalDropdowns from './components/ModalDropdowns';
import { getLayout, isLimitReached } from './utils/deviceModalUtils';

//TODO: Pending figma model update

/**
 * @param {onSubmit: (value: object), onBulkSubmit: (devices: object[]) => void, onCancel: (), device: object, message: {type: 'error' | 'message', message: string}, sending: boolean}
 */
export default function DeviceModal({
  onSubmit,
  onBulkSubmit,
  onCancel,
  device,
  message,
  loading,
}) {
  const { data: licenseInfo = {} } = useGetLicenseInfoQuery();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [formData, setFormData] = useState(
    device
      ? (() => {
          // A device that was bulk-added carries `bulk_mode` / `selected_devices`
          // in its stored properties (the backend keeps every unknown key of an
          // add payload). Neither is a property of the device: seeding the edit
          // form with them switched it into bulk mode and disabled the alias.
          const {
            bulk_mode: _bulkMode,
            selected_devices: _selectedDevices,
            ...deviceFields
          } = device;
          return {
            ...deviceFields,
            system_type: '',
            device_type: '',
            selected_device: device,
            new_node_id: device?.node_id ?? -1,
          };
        })()
      : {},
  );

  const [deviceTypes, setDeviceTypes] = useState([]);

  // Memoize layout computation to avoid repeatedly calling `getLayout`
  // on every render — it only needs to change when formData, integrations,
  // or deviceTypes change.
  const layoutConfig = useMemo(
    () => getLayout(formData, deviceTypes),
    [formData?.device_type, formData?.system_type, deviceTypes],
  );

  const coordinateMapComponents = Array.isArray(layoutConfig?.components)
    ? layoutConfig.components.filter(
        (component) => component && component.type === 'coordinate-map',
      )
    : [];
  const hasCoordinateMap = coordinateMapComponents.length > 0;

  // Check if license limit is reached (only for new devices, not editing)
  const isAtLicenseLimit = !device && isLimitReached(formData, deviceTypes, licenseInfo);

  /**
   * Which device type is the form describing, for the pairing control?
   *
   * On EDIT the type comes off the device itself, since the form's own type
   * selectors start empty in that case. A camera picks its siren; a siren picks
   * its camera; every other type shows no pairing control.
   */
  const selectedDeviceTypeName = device?.device_type ?? formData?.device_type;
  const pairingMode =
    selectedDeviceTypeName === CAMERA_TYPE
      ? 'camera'
      : selectedDeviceTypeName === SIREN_TYPE
        ? 'siren'
        : null;

  // True when the user has flipped the bulk-add toggle on for a supported device type.
  const isBulkSelect = formData?.bulk_mode === true;

  const canSubmit = () => {
    if (!device && isLimitReached(formData, deviceTypes, licenseInfo)) {
      return false;
    }

    const layoutItem = getLayout(formData, deviceTypes) || null;
    const selectedDeviceType = formData?.device_type;

    // Bulk multi-select path: system + device type chosen and at least one item checked.
    if (isBulkSelect) {
      return (
        !!formData.system_type && !!formData.device_type && formData.selected_devices.length > 0
      );
    }

    if (selectedDeviceType === 'Manual Road Closure') {
      if (formData?.coordinates) {
        const parsedCoords = tryParseString(formData?.coordinates);
        if (
          !parsedCoords ||
          !parsedCoords?.length ||
          !parsedCoords[0] ||
          !parsedCoords[0].coordinates ||
          !parsedCoords[0].coordinates.length
        ) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (layoutItem) {
      return !['system_type', 'device_type', ...(layoutItem['required-fields'] || [])]
        .map((key) => !!formData[key])
        .includes(false);
    } else {
      return !['system_type', 'device_type'].map((key) => !!formData[key]).includes(false);
    }
  };

  // convert formdata to device object on submit
  const submit = () => {
    if (isLimitReached(formData, deviceTypes, licenseInfo)) {
      console.warn(`Device cap reached for selected device cannot submit.`);
      return;
    }

    // The form already stores the type names the payload needs.
    const resolvedSystemType = formData.system_type;
    const resolvedDeviceType = formData.device_type;

    if (!resolvedSystemType || !resolvedDeviceType) {
      console.error('Unable to resolve selected system/device type from modal form state.', {
        formData,
        deviceTypes,
      });
      return;
    }

    // Form-only state must not travel: the backend keeps every unknown key of
    // the payload in `RI_Device.data`, and a bulk add would otherwise store the
    // whole checked list — all N rows — inside each of the N devices it creates.
    const { bulk_mode: _bulkMode, selected_devices: _selectedDevices, ...deviceFields } = formData;

    // Bulk add: build one payload per selected item and hand off to onBulkSubmit.
    if (isBulkSelect && formData.selected_devices.length > 0) {
      const payloads = formData.selected_devices.map((selectedDevice) => ({
        ...deviceFields,
        device: selectedDevice,
        alias: selectedDevice.name || '',
        system_type: resolvedSystemType,
        device_type: resolvedDeviceType,
      }));
      onBulkSubmit?.(payloads);
      return;
    }

    // If alias was left blank, fall back to the selected station's name so it is
    // never stored as null/empty — consistent with the Station Name fallback requirement.
    const resolvedAlias = formData.alias?.trim() || formData.device?.name || '';
    onSubmit({
      ...deviceFields,
      alias: resolvedAlias,
      system_type: resolvedSystemType,
      device_type: resolvedDeviceType,
    });
  };

  const licenseTooltip = (
    <div
      style={{
        backgroundColor: 'black',
        padding: 5,
        borderRadius: 3,
        textAlign: 'left',
        width: '500px',
        maxWidth: '90vw',
        zIndex: 'var(--z-popover)',
      }}
    >
      No remaining Device Licenses are available within your Organization. Either remove an existing
      Connected Device or contact the RescAlert Sales Team through our helpdesk (at the bottom
      right-side of the side bar) or contact us via email at overwatch.support@overwatch.ai.
    </div>
  );

  const formContent = (
    <Form className="d-flex flex-column flex-xl-row gap-3">
      {/* Left Section */}
      <div className="d-flex flex-column w-100">
        <ModalDropdowns
          deviceTypes={deviceTypes}
          formData={formData}
          setFormData={setFormData}
          setDeviceTypes={setDeviceTypes}
          device={device}
          layoutConfig={layoutConfig}
        />

        {/* One pairing cannot apply to many devices: hidden while bulk-adding. */}
        {pairingMode && !isBulkSelect && (
          <DevicePairing
            mode={pairingMode}
            formData={formData}
            setFormData={setFormData}
            device={device}
          />
        )}

        {(onSubmit || onBulkSubmit) && (
          <div className="d-flex justify-content-sm-end justify-content-center mb-3">
            {isAtLicenseLimit ? (
              <OverlayTrigger placement="top" overlay={licenseTooltip}>
                <div className="w-100 w-sm-auto" style={{ pointerEvents: 'auto' }}>
                  <Button
                    disabled={true}
                    className="border-0 mt-3 py-2 px-4 rounded-4 w-100"
                    style={{ cursor: 'not-allowed' }}
                  >
                    {device ? 'Submit' : 'Connect'}
                  </Button>
                </div>
              </OverlayTrigger>
            ) : (
              <Button
                disabled={!canSubmit()}
                className="border-0 mt-3 py-2 px-4 rounded-4 w-100 w-sm-auto"
                onClick={submit}
              >
                {device
                  ? 'Submit'
                  : isBulkSelect && formData.selected_devices?.length > 1
                    ? `Add ${formData.selected_devices.length} Devices`
                    : 'Connect'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Right Section */}
      {hasCoordinateMap && (
        <div className="w-100">
          {coordinateMapComponents.map((component, index) => (
            <RoadDefinerMap
              key={component['data-field'] || index}
              value={tryParseString(formData[component['data-field']])}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  [component['data-field']]: value,
                })
              }
              initialCenter={device?.location ? device.location : null}
            />
          ))}
        </div>
      )}
    </Form>
  );

  return (
    <BaseModal
      show={true}
      size={device ? 'xl' : 'lg'}
      handleShow={onCancel}
      title={device ? 'Edit Device' : 'New Device'}
    >
      <div style={isAtLicenseLimit ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
        {formContent}
      </div>

      {message && (
        <p className="response-message" data-type={message.type}>
          {message.message}
        </p>
      )}
      {loading && (
        <div>
          <WidgetLoadingAnimation disableText />
        </div>
      )}
    </BaseModal>
  );
}
